import { QueryClient } from '@tanstack/react-query';
import { API_ERROR_CODES } from '@hrms/shared';
import { toApiError } from './api-client';

/**
 * Shared query defaults.
 *
 * The important one is `retry`: TanStack Query retries three times by default,
 * which for a 403 means three identical refusals and a three-second delay
 * before the user sees the error. Only genuinely transient failures are worth
 * retrying.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // HR data changes on human timescales; a minute of staleness avoids
        // refetching the employee list every time a tab regains focus.
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (failureCount >= 2) return false;

          const { statusCode, code } = toApiError(error);

          // Client errors will not resolve themselves.
          if (statusCode >= 400 && statusCode < 500) return false;
          // Already handled by the axios refresh interceptor.
          if (code === API_ERROR_CODES.TOKEN_EXPIRED) return false;

          return true;
        },
      },
      mutations: {
        // Never automatic. A retried POST can create two leave requests.
        retry: false,
      },
    },
  });
}
